// src/components/reviews/ReviewsShadcn.jsx
import React, { useEffect, useState } from 'react';
import { Star, MapPin, ExternalLink, RefreshCw, Filter, Search } from 'lucide-react';

// shadcn components
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Spinner } from '../ui/spinner';

// Layout
import PageContainer from '../ui/layout/PageContainer/PageContainer';

// GBP hook
import { useGBP } from '../../hooks/useGBP';

// Connection gate component
import GBPConnectionGate from './components/GBPConnectionGate';

// Review components
import ReviewCard from './components/ReviewCard';
import ReviewReplyModal from './components/ReviewReplyModal';
import ReviewStats from './components/ReviewStats';

/**
 * Reviews Dashboard - Main reviews management interface
 *
 * Features:
 * - Connection gate (if not connected to GBP)
 * - Location selector (for multi-location businesses)
 * - Review stats (average rating, total reviews, pending replies)
 * - Review list with filters
 * - Reply modal
 */
const ReviewsShadcn = () => {
  const {
    // Connection status
    isConnected,
    connected,
    gbpConnectionLoading,
    connectedLocations,
    primaryLocation,

    // Reviews
    reviews,
    reviewsLoading,
    reviewsError,
    reviewsPagination,
    reviewStats,

    // Operations
    loadGBPConnectionStatus,
    fetchGBPReviews,
    replyToGBPReview,
    deleteGBPReviewReply,

    // Review operations state
    replyingToReview,
    deletingReplyForReview
  } = useGBP();

  // Local UI state
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [filterRating, setFilterRating] = useState('all');
  const [filterReplied, setFilterReplied] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // ================================
  // LOAD CONNECTION STATUS ON MOUNT
  // ================================

  useEffect(() => {
    console.log('📋 Reviews: Loading connection status');
    loadGBPConnectionStatus();
  }, [loadGBPConnectionStatus]);

  // ================================
  // AUTO-SELECT PRIMARY LOCATION
  // ================================

  useEffect(() => {
    if (connected && connectedLocations.length > 0 && !selectedLocation) {
      const primary = primaryLocation || connectedLocations[0];
      console.log('📍 Reviews: Auto-selecting location:', primary.locationName);
      setSelectedLocation(primary);
    }
  }, [connected, connectedLocations, primaryLocation, selectedLocation]);

  // ================================
  // LOAD REVIEWS WHEN LOCATION SELECTED
  // ================================

  useEffect(() => {
    if (selectedLocation) {
      console.log('⭐ Reviews: Loading reviews for:', selectedLocation.locationName);
      fetchGBPReviews(selectedLocation.name);
    }
  }, [selectedLocation, fetchGBPReviews]);

  // ================================
  // HANDLERS
  // ================================

  const handleLocationChange = (locationName) => {
    const location = connectedLocations.find(loc => loc.name === locationName);
    if (location) {
      setSelectedLocation(location);
    }
  };

  const handleRefresh = () => {
    if (selectedLocation) {
      fetchGBPReviews(selectedLocation.name);
    }
  };

  const handleReplyClick = (review) => {
    setSelectedReview(review);
    setReplyModalOpen(true);
  };

  const handleReplySubmit = (replyText) => {
    if (selectedLocation && selectedReview) {
      replyToGBPReview(selectedLocation.name, selectedReview.reviewId, replyText);
      // Modal will close when reply succeeds via socket event
    }
  };

  const handleDeleteReply = (review) => {
    if (selectedLocation && review) {
      deleteGBPReviewReply(selectedLocation.name, review.reviewId);
    }
  };

  // ================================
  // FILTER REVIEWS
  // ================================

  const filteredReviews = reviews.filter(review => {
    // Filter by rating
    if (filterRating !== 'all') {
      const rating = parseInt(filterRating);
      if (review.starRating !== rating) return false;
    }

    // Filter by reply status
    if (filterReplied === 'replied' && !review.reviewReply) return false;
    if (filterReplied === 'pending' && review.reviewReply) return false;

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const reviewerName = review.reviewer?.displayName?.toLowerCase() || '';
      const reviewText = review.comment?.toLowerCase() || '';
      if (!reviewerName.includes(query) && !reviewText.includes(query)) {
        return false;
      }
    }

    return true;
  });

  // ================================
  // RENDER: LOADING STATE
  // ================================

  if (gbpConnectionLoading) {
    return (
      <PageContainer
        title="Reviews"
        subtitle="Manage your Google Business Profile reviews"
      >
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Spinner className="size-12 text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
      </PageContainer>
    );
  }

  // ================================
  // RENDER: NOT CONNECTED (CONNECTION GATE)
  // ================================

  if (!connected) {
    return (
      <PageContainer
        title="Reviews"
        subtitle="Manage your Google Business Profile reviews"
      >
        <GBPConnectionGate />
      </PageContainer>
    );
  }

  // ================================
  // RENDER: CONNECTED (REVIEWS DASHBOARD)
  // ================================

  return (
    <PageContainer
      title="Reviews"
      subtitle="Manage your Google Business Profile reviews"
    >
      <div className="space-y-6 max-w-7xl">
        {/* Header Controls */}
        <div className="flex items-center justify-between gap-4">
          {/* Location Selector */}
          <div className="flex items-center gap-3">
            <MapPin className="h-5 w-5 text-gray-500" />
            {connectedLocations.length === 1 ? (
              <div>
                <p className="font-medium">{selectedLocation?.locationName}</p>
                <p className="text-sm text-gray-600">{selectedLocation?.address?.locality}</p>
              </div>
            ) : (
              <Select
                value={selectedLocation?.name}
                onValueChange={handleLocationChange}
              >
                <SelectTrigger className="w-[300px]">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {connectedLocations.map(location => (
                    <SelectItem key={location.name} value={location.name}>
                      <div className="flex flex-col">
                        <span className="font-medium">{location.locationName}</span>
                        <span className="text-xs text-gray-600">{location.address?.locality}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Refresh Button */}
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={reviewsLoading}
          >
            {reviewsLoading ? (
              <Spinner className="size-4 mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>

        {/* Review Stats */}
        <ReviewStats
          averageRating={reviewStats.averageRating}
          totalReviewCount={reviewStats.totalReviewCount}
          pendingRepliesCount={reviews.filter(r => !r.reviewReply).length}
        />

        {/* Error Alert */}
        {reviewsError && (
          <Alert variant="destructive">
            <AlertDescription>{reviewsError}</AlertDescription>
          </Alert>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
                <Input
                  placeholder="Search reviews..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Rating Filter */}
              <Select value={filterRating} onValueChange={setFilterRating}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by rating" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Ratings</SelectItem>
                  <SelectItem value="5">5 Stars</SelectItem>
                  <SelectItem value="4">4 Stars</SelectItem>
                  <SelectItem value="3">3 Stars</SelectItem>
                  <SelectItem value="2">2 Stars</SelectItem>
                  <SelectItem value="1">1 Star</SelectItem>
                </SelectContent>
              </Select>

              {/* Reply Status Filter */}
              <Select value={filterReplied} onValueChange={setFilterReplied}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Reviews</SelectItem>
                  <SelectItem value="pending">Pending Reply</SelectItem>
                  <SelectItem value="replied">Replied</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Reviews List */}
        {reviewsLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner className="size-8 text-blue-600" />
          </div>
        ) : filteredReviews.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Star className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">
                {reviews.length === 0
                  ? 'No reviews yet'
                  : 'No reviews match your filters'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredReviews.map(review => (
              <ReviewCard
                key={review.reviewId}
                review={review}
                onReplyClick={handleReplyClick}
                onDeleteReply={handleDeleteReply}
                isReplying={replyingToReview === review.reviewId}
                isDeletingReply={deletingReplyForReview === review.reviewId}
              />
            ))}
          </div>
        )}

        {/* Pagination (if needed) */}
        {reviewsPagination?.nextPageToken && (
          <div className="flex justify-center">
            <Button
              variant="outline"
              onClick={() => fetchGBPReviews(selectedLocation.name, {
                pageToken: reviewsPagination.nextPageToken
              })}
              disabled={reviewsLoading}
            >
              Load More
            </Button>
          </div>
        )}
      </div>

      {/* Reply Modal */}
      <ReviewReplyModal
        isOpen={replyModalOpen}
        onClose={() => {
          setReplyModalOpen(false);
          setSelectedReview(null);
        }}
        review={selectedReview}
        onSubmit={handleReplySubmit}
        isSubmitting={replyingToReview === selectedReview?.reviewId}
      />
    </PageContainer>
  );
};

export default ReviewsShadcn;
